import type { BuildingInStoreInfo, FloorInfo } from '@/services/dto';
import type { PoiContentSlide } from '@/types/shop';
import poiUtils from './poi';
import type { FacilityFilterConfig } from '@/stores/types/facilityStore';
import { FACILITY_SEARCH_MODE } from '@/enums/facility';

export const convertPoiContentToSlide = (lang: string, buildings: BuildingInStoreInfo[]): PoiContentSlide[] => {
    const allPois = buildings.flatMap((building) =>
        (building.filterPoiContents.facility ?? []).map((poi) => ({
            poi,
            building,
        }))
    );

    return allPois.map(({ poi, building }) => {
        const contents = poi.contents;

        const poiFormatData = {
            name: poi.name?.[lang] ? poi.name?.[lang] : poi.name?.['ko'],
            facilityUrl: contents.shopTmrkImgUrlApp?.[lang] ? contents.shopTmrkImgUrlApp?.[lang] : contents.shopTmrkImgUrlApp?.['ko'],
            buildingName: building?.name?.[lang] ? building?.name?.[lang] : building?.name?.['ko'],
        };

        const name = poiFormatData.name || '';
        const facilityUrl = poiFormatData.facilityUrl || '';
        const floor = contents.cstrFlrNm?.[lang] || poiUtils.getFloorName(lang, poi.studioFloorId, building.floors ?? []);
        const buildingName = poiFormatData.buildingName || '';

        return {
            id: poi.id,
            name,
            buildingName,
            floor,
            url: facilityUrl,
            data: poi,
            poiType: 'facility',
        };
    });
};

export const filterByFloor = (facility: PoiContentSlide, config: FacilityFilterConfig): boolean => {
    const { selectedBuildingId, storeCode, buildingCode, selectedFloor, buildings } = config;

    // Filter by storeCode and buildingCode using cstrCd.ko and cstrTownCd.ko
    if (storeCode != null && buildingCode != null) {
        const poiCstrCd = facility.data.contents.cstrCd?.ko || '';
        const poiCstrTownCd = facility.data.contents.cstrTownCd?.ko || '';

        // Filter by storeCode (cstrTownCd.ko) and buildingCode (cstrCd.ko)
        if (poiCstrCd !== storeCode || poiCstrTownCd !== buildingCode) {
            return false;
        }

        // Now filter by floor using cstrFlrNm instead of studioFloorId
        if (selectedFloor?.id === 'all') {
            return true;
        }

        // Find building from storeCode and buildingCode to get floors
        const building = buildings?.find((b) => b.storeCode === storeCode && b.buildingCode === buildingCode);
        if (!building) return false;

        // Get floor name from POI content
        const poiFloorName = facility.data.contents.cstrFlrNm?.ko || poiUtils.getFloorName('ko', facility?.data?.studioFloorId, building.floors) || '';

        // Get selected floor name
        const selectedFloorInfo = selectedFloor?.data as FloorInfo;
        if (!selectedFloorInfo) return false;

        const selectedFloorName = selectedFloorInfo.name?.ko || '';

        // Compare floor names
        return poiFloorName === selectedFloorName;
    }

    // Fallback: Case: specific building selected (old logic)
    if (selectedBuildingId && selectedBuildingId !== 'all') {
        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;

        if (selectedFloor?.id === 'all') {
            return facility.data.studioBuildingId === building.studioBuildingId;
        }

        return facility.data.studioFloorId === (selectedFloor?.data as FloorInfo)?.floorCode && facility.data.studioBuildingId === building.studioBuildingId;
    }

    // Case: all buildings mode
    if (selectedBuildingId === 'all') {
        if (selectedFloor?.id === 'all') return true;

        if (Array.isArray(selectedFloor?.data)) {
            return (selectedFloor.data as string[]).includes(facility.data.studioFloorId);
        }

        return false;
    }

    return false;
};

export const filterByCategory = (facility: PoiContentSlide, config: FacilityFilterConfig): boolean => {
    const { selectedCategory, storeCode, buildingCode, selectedBuildingId, buildings } = config;

    if (!selectedCategory) return false;

    // Filter by storeCode and buildingCode using cstrCd.ko and cstrTownCd.ko
    if (storeCode != null && buildingCode != null) {
        const poiCstrCd = facility.data.contents.cstrCd?.ko || '';
        const poiCstrTownCd = facility.data.contents.cstrTownCd?.ko || '';

        // Filter by storeCode (cstrTownCd.ko) and buildingCode (cstrCd.ko)
        if (poiCstrTownCd !== buildingCode || poiCstrCd !== storeCode) {
            return false;
        }
    } else if (selectedBuildingId && selectedBuildingId !== 'all') {
        // Fallback: use selectedBuildingId if storeCode/buildingCode not available
        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;
        if (facility.data.studioBuildingId !== building.studioBuildingId) {
            return false;
        }
    }

    if (selectedCategory.clientCode === 'all') {
        return true;
    }

    const building = buildings?.find((b) => b.studioBuildingId === facility.data.studioBuildingId);

    if (selectedCategory.categoryCode) {
        return facility.data.categories.includes(selectedCategory.categoryCode);
    } else {
        const foundCategory = building?.buildingCategories.find((c) => c.clientCode === selectedCategory.clientCode);
        return facility.data.categories.includes(foundCategory?.categoryCode || '');
    }
};

export const createFacilityFilter = (searchMode: FACILITY_SEARCH_MODE, config: FacilityFilterConfig) => {
    const filterMap = {
        [FACILITY_SEARCH_MODE.SEARCH_BY_FLOOR]: filterByFloor,
        [FACILITY_SEARCH_MODE.SEARCH_BY_CATEGORY]: filterByCategory,
    };

    const filterFunction = filterMap[searchMode];
    return (facility: PoiContentSlide) => {
        return filterFunction ? filterFunction(facility, config) : true;
    };
};

const facilityUtils = {
    convertPoiContentToSlide,
    filterByFloor,
    filterByCategory,
    createFacilityFilter,
};

export default facilityUtils;
